import { useCallback, useEffect, useRef, useState } from "react";

import { useNavigate, useParams } from "react-router-dom";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import {
  ArrowLeft,
  Check,
  ChevronRight,
  FileText,
  Loader2,
  MapPin,
  Minus,
  Package,
  Plus,
  ShoppingCart,
  Truck,
  X,
} from "lucide-react";
import Swal from "sweetalert2";

import { PageContainer } from "~/components/layout/PageContainer";
import { PageHeader } from "~/components/layout/PageHeader";
import { Button } from "~/components/ui/button";
import { Card, CardContent } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { Progress } from "~/components/ui/progress";
import { Skeleton } from "~/components/ui/skeleton";
import { useToast } from "~/hooks/use-toast";
import { ingestionService } from "~/services/wms/ingestionService";
import { pickingService } from "~/services/wms/pickingService";
import { useProfileStore } from "~/store/profileStore";
import type { PickTaskStatus } from "~/types/wms";

type WorkflowStep = "loading" | "summary" | "picking" | "completed";

/** Strip warehouse prefix (e.g. "BP-B1-02-A" → "B1-02-A") */
function stripPrefix(code: string): string {
  const idx = code.indexOf("-");
  return idx !== -1 ? code.slice(idx + 1) : code;
}

interface PickItemDisplay {
  id: string;
  product_id: string;
  requested_quantity: number;
  picked_quantity: number;
  status: string;
  pick_sequence: number;
  lot_number?: string;
  source_position_id?: string;
  scanned_location: boolean;
  product?: { id: string; short_name: string };
  source_position?: { id: string; full_code: string; barcode?: string };
}

interface PickTaskDisplay {
  id: string;
  status: PickTaskStatus;
  priority: number;
  created_at: string;
  started_at?: string;
  completed_at?: string;
  sales_request?: {
    id: string;
    client?: { name: string };
    delivery_date?: string;
  };
  items?: PickItemDisplay[];
}

export function PickingWorkflowPage() {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const profileId = useProfileStore((s) => s.id);

  const [currentStep, setCurrentStep] = useState<WorkflowStep>("loading");
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  const [locationInput, setLocationInput] = useState("");
  const [pickedQuantity, setPickedQuantity] = useState(0);
  const [locationVerified, setLocationVerified] = useState(false);

  const locationInputRef = useRef<HTMLInputElement>(null);
  const isGeneratingInvoiceRef = useRef(false); // Synchronous guard to prevent double-clicks

  // Fetch pick task details
  const { data: task, isLoading } = useQuery({
    queryKey: ["pick-task-detail", taskId],
    queryFn: () => pickingService.getPickTaskDetail(taskId || ""),
    enabled: !!taskId,
  });

  // Start task mutation
  const startMutation = useMutation({
    mutationFn: () => pickingService.startPickTask(taskId || ""),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pick-task-detail", taskId] });
      setCurrentStep("picking");
      setCurrentItemIndex(0);
    },
    onError: (err) => {
      toast({
        title: "Error",
        description:
          err instanceof Error ? err.message : "No se pudo iniciar la tarea",
        variant: "destructive",
      });
    },
  });

  // Pick item mutation
  const pickItemMutation = useMutation({
    mutationFn: ({
      itemId,
      quantity,
      userId,
    }: {
      itemId: string;
      quantity: number;
      userId: string;
    }) => pickingService.pickItem(itemId, quantity, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pick-task-detail", taskId] });

      // Move to next item or complete
      const items = task?.items || [];
      const pendingItems = items.filter(
        (i) => i.status === "pending" && i.id !== items[currentItemIndex]?.id
      );

      if (pendingItems.length === 0) {
        // All items picked, complete task
        completeMutation.mutate();
      } else {
        // Find next pending item
        const nextIndex = items.findIndex(
          (i) => i.status === "pending" && i.id !== items[currentItemIndex]?.id
        );
        if (nextIndex >= 0) {
          setCurrentItemIndex(nextIndex);
          resetItemState();
        }
      }
    },
    onError: (err) => {
      toast({
        title: "Error",
        description:
          err instanceof Error ? err.message : "Error al registrar picking",
        variant: "destructive",
      });
    },
  });

  // Complete task mutation
  const completeMutation = useMutation({
    mutationFn: () => pickingService.completePickTask(taskId || ""),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pick-task-detail", taskId] });
      queryClient.invalidateQueries({ queryKey: ["worker-pick-tasks"] });
      setCurrentStep("completed");
    },
    onError: (err) => {
      toast({
        title: "Error",
        description:
          err instanceof Error ? err.message : "Error al completar tarea",
        variant: "destructive",
      });
    },
  });

  // Generate invoice mutation
  const generateInvoiceMutation = useMutation({
    mutationFn: () => pickingService.generateInvoiceAfterPicking(taskId || ""),
    onSuccess: (result) => {
      if (result.success) {
        toast({
          title: "Factura generada",
          description: "La factura se ha generado correctamente",
        });
        queryClient.invalidateQueries({
          queryKey: ["pick-task-detail", taskId],
        });
        queryClient.invalidateQueries({ queryKey: ["picking-orders"] });
        navigate("/wms/picking-orders");
      } else {
        Swal.fire({
          icon: "error",
          title: "Error al generar factura",
          text: result.error || "Error desconocido",
          confirmButtonColor: "#ef4444",
        });
      }
    },
    onError: (err) => {
      Swal.fire({
        icon: "error",
        title: "Error al generar factura",
        text: err instanceof Error ? err.message : "Error al generar factura",
        confirmButtonColor: "#ef4444",
      });
    },
  });

  // Initialize step based on task status
  useEffect(() => {
    if (!isLoading && task) {
      if (task.status === "completed") {
        setCurrentStep("completed");
      } else if (task.status === "in_progress") {
        setCurrentStep("picking");
        // Find first pending item
        const pendingIndex =
          task.items?.findIndex((i) => i.status === "pending") ?? 0;
        setCurrentItemIndex(pendingIndex >= 0 ? pendingIndex : 0);
      } else {
        setCurrentStep("summary");
      }
    }
  }, [isLoading, task]);

  const resetItemState = () => {
    setLocationInput("");
    setPickedQuantity(0);
    setLocationVerified(false);
    setTimeout(() => locationInputRef.current?.focus(), 100);
  };

  const handleLocationSubmit = useCallback(async () => {
    if (!locationInput.trim()) return;

    const currentItem = task?.items?.[currentItemIndex];
    if (!currentItem?.source_position) return;

    const result = await ingestionService.lookupPositionByBarcode(
      locationInput.trim()
    );

    if (result.error || !result.position) {
      toast({
        title: "Ubicacion no encontrada",
        description: result.error || "Posicion invalida",
        variant: "destructive",
      });
      setLocationInput("");
      locationInputRef.current?.focus();
      return;
    }

    if (result.position.id === currentItem.source_position.id) {
      setLocationVerified(true);
      setPickedQuantity(currentItem.requested_quantity);
      toast({
        title: "Ubicacion verificada",
        description: `Recoge ${currentItem.requested_quantity} cajas de ${currentItem.product?.short_name}`,
      });
    } else {
      toast({
        title: "Ubicacion incorrecta",
        description: `Esperado: ${stripPrefix(currentItem.source_position.full_code)}`,
        variant: "destructive",
      });
      setLocationInput("");
      locationInputRef.current?.focus();
    }
  }, [locationInput, task, currentItemIndex, toast, locationInputRef]);

  const handleLocationKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleLocationSubmit();
    }
  };

  const handleConfirmPick = () => {
    if (!profileId || !task?.items?.[currentItemIndex]) return;

    const currentItem = task.items[currentItemIndex];
    pickItemMutation.mutate({
      itemId: currentItem.id,
      quantity: pickedQuantity,
      userId: profileId,
    });
  };

  const handleBackToTasks = () => {
    navigate("/wms");
  };

  const handleGenerateInvoice = () => {
    // Synchronous guard - prevents race condition from double-clicks
    if (isGeneratingInvoiceRef.current) return;
    isGeneratingInvoiceRef.current = true;

    generateInvoiceMutation.mutate(undefined, {
      onSettled: () => {
        isGeneratingInvoiceRef.current = false;
      },
    });
  };

  // Loading state
  if (isLoading || currentStep === "loading") {
    return (
      <PageContainer>
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </PageContainer>
    );
  }

  if (!task) {
    return (
      <PageContainer>
        <Card>
          <CardContent className="flex flex-col items-center gap-4 py-8">
            <Package className="h-10 w-10 text-muted-foreground" />
            <p className="text-muted-foreground">Tarea no encontrada</p>
            <Button onClick={handleBackToTasks}>Volver a tareas</Button>
          </CardContent>
        </Card>
      </PageContainer>
    );
  }

  const typedTask = task as PickTaskDisplay;
  const items = typedTask.items || [];
  const pendingItems = items.filter((i) => i.status === "pending");
  const pickedItems = items.filter(
    (i) => i.status === "picked" || i.status === "short"
  );
  const totalQuantity = items.reduce(
    (sum, i) => sum + (i.requested_quantity || 0),
    0
  );
  const pickedQuantityTotal = items.reduce(
    (sum, i) => sum + (i.picked_quantity || 0),
    0
  );
  const progressPercent =
    items.length > 0 ? (pickedItems.length / items.length) * 100 : 0;

  return (
    <PageContainer>
      <PageHeader
        title="Picking"
        description={
          currentStep === "summary"
            ? "Resumen del pedido"
            : currentStep === "picking"
              ? `Item ${pickedItems.length + 1} de ${items.length}`
              : "Pedido completado"
        }
        actions={
          currentStep !== "completed" && (
            <Button variant="outline" size="sm" onClick={handleBackToTasks}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Cancelar
            </Button>
          )
        }
      />

      {/* Progress bar */}
      {currentStep === "picking" && (
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Progreso</span>
            <span className="font-medium">
              {pickedItems.length}/{items.length} items
            </span>
          </div>
          <Progress value={progressPercent} className="h-2" />
        </div>
      )}

      {/* Content based on step */}
      {currentStep === "summary" && (
        <SummaryStep
          task={typedTask}
          items={items}
          totalQuantity={totalQuantity}
          onStart={() => startMutation.mutate()}
          isLoading={startMutation.isPending}
        />
      )}

      {currentStep === "picking" && items[currentItemIndex] && (
        <PickingStep
          item={items[currentItemIndex]}
          itemNumber={pickedItems.length + 1}
          totalItems={items.length}
          locationInput={locationInput}
          setLocationInput={setLocationInput}
          locationInputRef={locationInputRef}
          onLocationKeyPress={handleLocationKeyPress}
          onLocationSubmit={handleLocationSubmit}
          locationVerified={locationVerified}
          pickedQuantity={pickedQuantity}
          setPickedQuantity={setPickedQuantity}
          onConfirmPick={handleConfirmPick}
          isLoading={pickItemMutation.isPending}
        />
      )}

      {currentStep === "completed" && (
        <CompletedStep
          task={typedTask}
          items={items}
          totalQuantity={totalQuantity}
          pickedQuantity={pickedQuantityTotal}
          onBackToTasks={handleBackToTasks}
          onGenerateInvoice={handleGenerateInvoice}
          isGeneratingInvoice={generateInvoiceMutation.isPending}
        />
      )}
    </PageContainer>
  );
}

// ============================================================
// Summary Step
// ============================================================

function SummaryStep({
  task,
  items,
  totalQuantity,
  onStart,
  isLoading,
}: {
  task: PickTaskDisplay;
  items: PickItemDisplay[];
  totalQuantity: number;
  onStart: () => void;
  isLoading: boolean;
}) {
  const clientName = task.sales_request?.client?.name || "Cliente";
  const deliveryDate = task.sales_request?.delivery_date
    ? dayjs(task.sales_request.delivery_date).format("DD/MM/YYYY")
    : null;

  return (
    <div className="space-y-4">
      {/* Order info */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
              <Truck className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="font-medium">{clientName}</p>
              {deliveryDate && (
                <p className="text-sm text-muted-foreground">
                  Entrega: {deliveryDate}
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Items summary */}
      <Card>
        <CardContent className="p-4">
          <h3 className="mb-3 font-medium">Items a preparar</h3>
          <div className="space-y-2">
            {items.map((item, index) => (
              <div
                key={item.id}
                className="flex items-center justify-between rounded-lg border p-3"
              >
                <div className="flex items-center gap-3">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-100 text-xs font-medium">
                    {index + 1}
                  </span>
                  <div>
                    <p className="text-sm font-medium">
                      {item.product?.short_name || "Producto"}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      <MapPin className="mr-1 inline h-3 w-3" />
                      {item.source_position
                        ? stripPrefix(item.source_position.full_code)
                        : "Sin ubicacion"}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-medium">{item.requested_quantity}</p>
                  <p className="text-xs text-muted-foreground">cajas</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 flex justify-between border-t pt-3">
            <span className="font-medium">Total</span>
            <span className="font-bold">{totalQuantity} cajas</span>
          </div>
        </CardContent>
      </Card>

      {/* Start button */}
      <Button
        className="w-full"
        size="lg"
        onClick={onStart}
        disabled={isLoading}
      >
        {isLoading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Iniciando...
          </>
        ) : (
          <>
            Comenzar Picking
            <ChevronRight className="ml-2 h-4 w-4" />
          </>
        )}
      </Button>
    </div>
  );
}

// ============================================================
// Picking Step
// ============================================================

function PickingStep({
  item,
  itemNumber,
  totalItems,
  locationInput,
  setLocationInput,
  locationInputRef,
  onLocationKeyPress,
  onLocationSubmit,
  locationVerified,
  pickedQuantity,
  setPickedQuantity,
  onConfirmPick,
  isLoading,
}: {
  item: PickItemDisplay;
  itemNumber: number;
  totalItems: number;
  locationInput: string;
  setLocationInput: (value: string) => void;
  locationInputRef: React.RefObject<HTMLInputElement>;
  onLocationKeyPress: (e: React.KeyboardEvent) => void;
  onLocationSubmit: () => void;
  locationVerified: boolean;
  pickedQuantity: number;
  setPickedQuantity: (value: number) => void;
  onConfirmPick: () => void;
  isLoading: boolean;
}) {
  const productName = item.product?.short_name || "Producto";
  const locationCode = item.source_position
    ? stripPrefix(item.source_position.full_code)
    : "Sin ubicacion";

  return (
    <div className="space-y-4">
      {/* Main instruction - imperative */}
      <Card className="border-green-200 bg-green-50">
        <CardContent className="p-5">
          <div className="mb-2 flex items-center justify-between text-sm text-muted-foreground">
            <span>
              Item {itemNumber} de {totalItems}
            </span>
            {item.lot_number && (
              <span className="rounded bg-white px-2 py-1 text-xs font-medium">
                Lote: {item.lot_number}
              </span>
            )}
          </div>

          {/* Imperative instruction */}
          <div className="rounded-lg bg-white p-4 text-center shadow-sm">
            <p className="text-lg font-semibold text-gray-700">
              Recoge{" "}
              <span className="text-2xl font-bold text-green-600">
                {item.requested_quantity}
              </span>{" "}
              cajas
            </p>
            <p className="mt-1 text-base font-medium text-gray-800">
              {productName}
            </p>
            {item.lot_number && (
              <p className="mt-1 text-sm text-gray-500">
                del Lote <span className="font-medium">{item.lot_number}</span>
              </p>
            )}
            <p className="mt-2 text-sm text-gray-500">
              en posición{" "}
              <span className="text-lg font-bold text-blue-600">
                {locationCode}
              </span>
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Location verification */}
      <Card>
        <CardContent className="p-4">
          <div className="mb-4 text-center">
            <div className="mb-2 flex items-center justify-center gap-2">
              <MapPin className="h-5 w-5 text-blue-500" />
              <span className="text-sm text-muted-foreground">
                Verificar ubicación:
              </span>
            </div>
            <p className="text-2xl font-bold">{locationCode}</p>
          </div>

          {!locationVerified ? (
            <div className="space-y-3">
              <p className="text-center text-sm text-muted-foreground">
                Escanea el codigo de ubicacion para verificar
              </p>
              <div className="relative">
                <Input
                  ref={locationInputRef}
                  value={locationInput}
                  onChange={(e) => setLocationInput(e.target.value)}
                  onKeyPress={onLocationKeyPress}
                  placeholder="Escanear ubicacion..."
                  className="pr-10 text-center font-mono text-lg"
                  autoFocus
                />
                {locationInput && (
                  <button
                    type="button"
                    onClick={() => setLocationInput("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              <Button
                className="w-full"
                onClick={onLocationSubmit}
                disabled={!locationInput.trim()}
              >
                Verificar Ubicacion
              </Button>
            </div>
          ) : (
            <div className="flex items-center justify-center gap-2 rounded-lg bg-green-100 py-3 text-green-700">
              <Check className="h-5 w-5" />
              <span className="font-medium">Ubicacion verificada</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quantity picker (only show after location verified) */}
      {locationVerified && (
        <Card>
          <CardContent className="p-4">
            <p className="mb-4 text-center text-sm text-muted-foreground">
              Cantidad recogida
            </p>
            <div className="flex items-center justify-center gap-4">
              <Button
                variant="outline"
                size="icon"
                onClick={() =>
                  setPickedQuantity(Math.max(0, pickedQuantity - 1))
                }
                disabled={isLoading}
              >
                <Minus className="h-4 w-4" />
              </Button>
              <span className="w-20 text-center text-3xl font-bold">
                {pickedQuantity}
              </span>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setPickedQuantity(pickedQuantity + 1)}
                disabled={
                  isLoading || pickedQuantity >= item.requested_quantity
                }
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            {pickedQuantity < item.requested_quantity && (
              <p className="mt-2 text-center text-sm text-yellow-600">
                Faltante: {item.requested_quantity - pickedQuantity} cajas
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Confirm button */}
      {locationVerified && (
        <Button
          className="w-full"
          size="lg"
          onClick={onConfirmPick}
          disabled={isLoading || pickedQuantity === 0}
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Guardando...
            </>
          ) : (
            <>
              Confirmar Picking
              <Check className="ml-2 h-4 w-4" />
            </>
          )}
        </Button>
      )}
    </div>
  );
}

// ============================================================
// Completed Step
// ============================================================

function CompletedStep({
  task,
  items,
  totalQuantity,
  pickedQuantity,
  onBackToTasks,
  onGenerateInvoice,
  isGeneratingInvoice,
}: {
  task: PickTaskDisplay;
  items: PickItemDisplay[];
  totalQuantity: number;
  pickedQuantity: number;
  onBackToTasks: () => void;
  onGenerateInvoice: () => void;
  isGeneratingInvoice: boolean;
}) {
  const clientName = task.sales_request?.client?.name || "Cliente";
  const completedAt = task.completed_at ? dayjs(task.completed_at) : dayjs();
  const startedAt = task.started_at ? dayjs(task.started_at) : completedAt;
  const duration = completedAt.diff(startedAt, "minute");

  const shortItems = items.filter((i) => i.status === "short");

  return (
    <div className="space-y-6 py-8">
      <div className="text-center">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
          <Check className="h-8 w-8 text-green-600" />
        </div>
        <h2 className="text-xl font-semibold">Picking Completado</h2>
        <p className="text-muted-foreground">{clientName}</p>
      </div>

      <Card>
        <CardContent className="space-y-3 p-4">
          <h3 className="font-medium text-gray-600">Resumen</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Items</span>
              <span className="font-medium">{items.length}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Cajas solicitadas</span>
              <span className="font-medium">{totalQuantity}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Cajas recogidas</span>
              <span className="font-medium">{pickedQuantity}</span>
            </div>
            {shortItems.length > 0 && (
              <div className="flex justify-between text-yellow-600">
                <span>Faltantes</span>
                <span className="font-medium">{shortItems.length} items</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-gray-500">Tiempo</span>
              <span className="font-medium">{duration} minutos</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="space-y-3">
        <Button
          className="w-full"
          size="lg"
          onClick={onGenerateInvoice}
          disabled={isGeneratingInvoice}
        >
          {isGeneratingInvoice ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Generando Factura...
            </>
          ) : (
            <>
              <FileText className="mr-2 h-4 w-4" />
              Generar Factura
            </>
          )}
        </Button>
        <Button variant="outline" className="w-full" onClick={onBackToTasks}>
          Volver a Tareas
        </Button>
      </div>
    </div>
  );
}

export default PickingWorkflowPage;
